import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LayoutDashboard, SearchCheck, BellRing, ListChecks } from "lucide-react";
import { Reveal, SectionTag } from "@/components/primitives";
import { MockDashboard } from "@/components/MockDashboard";
import { useLanguage } from "@/hooks/use-language";

const ICONS = [LayoutDashboard, SearchCheck, BellRing, ListChecks];

export const Deliverables = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section className="relative py-24 lg:py-32 border-b border-border" data-testid="deliverables-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <Reveal>
              <SectionTag index="04" label={t.deliver.tag} />
              <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl">
                {t.deliver.h2}
              </h2>
            </Reveal>
            <div className="mt-12 grid sm:grid-cols-2 gap-px bg-border border border-border">
              {t.deliver.cards.map((card, i) => {
                const Icon = ICONS[i];
                return (
                <div key={card.title} className="group bg-background p-8 hover:bg-secondary/60 transition-colors duration-500" data-testid={`deliverable-${i}`}>
                  <Icon size={24} strokeWidth={1.5} className="text-teal-600 dark:text-teal-400 group-hover:scale-110 transition-transform duration-500" />
                  <h3 className="mt-6 font-display font-bold text-base">{card.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
                </div>
                );
              })}
            </div>
          </div>

          <Reveal delay={0.2} className="lg:sticky lg:top-28">
            <div className="relative">
              <div className="absolute -inset-8 bg-teal-500/10 blur-3xl rounded-full -z-10" />
              <p className="font-display text-xs tracking-[0.3em] uppercase text-teal-600 dark:text-teal-400 mb-4">
                {t.deliver.liveExample}
              </p>
              <motion.div style={{ y: imgY }}>
                <MockDashboard />
              </motion.div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
